/**
 * The one read of the native Audience / Targeting page (spec §2.2): get_audience_facts (migration 124), one row per
 * ad set, paged by ad_set_id (one row per ad set, so unique). Every page or a throw: a partial list would show as a
 * shorter table nobody measured. Names are kept as stored (a NULL one reads ""); the native id and the currency follow
 * D2's normalisation: trimmed, and a blank one is null.
 *
 * @module services/audience/audienceReaders
 */
import { getSupabaseClient } from '@/config';
import { entityStatusOf, trimmedOrNull } from '@/services/channels/channelReaders';
import { isAdChannel, type AdChannel } from '@/services/channels/channelTypes';
import type { DateRange } from '@/services/looker/lookerDateRange';
import { nullableNumber } from '@/services/overview/overviewMath';
import { errorMessageOf, readAllRows } from '@/services/supabasePaging';
import type { AudienceFact } from './audienceTypes';

/** A get_audience_facts row; numeric may arrive as a string. */
export interface AudienceFactDb {
  workspace_id: string;
  platform: string;
  ad_set_id: string;
  external_ad_set_id: string | null;
  ad_set_name: string | null;
  ad_set_status: string | null;
  campaign_id: string;
  campaign_name: string | null;
  campaign_status: string | null;
  campaign_end_date: string | null;
  objective: string;
  currency: string | null;
  spend: number | string | null;
  impressions: number | string | null;
  clicks: number | string | null;
  last_date: string;
}

/** The RPC returns the four ad channels only; anything else means the function changed: fail the read, loudly. */
const platformOf = (v: string): AdChannel => {
  if (!isAdChannel(v)) throw new Error(`audience facts read failed: unknown platform "${v}"`);
  return v;
};

export const audienceFactOf = (r: AudienceFactDb): AudienceFact => ({
  workspaceId: r.workspace_id,
  platform: platformOf(r.platform),
  adSetId: r.ad_set_id,
  externalAdSetId: trimmedOrNull(r.external_ad_set_id),
  adSetName: r.ad_set_name ?? '',
  adSetStatus: entityStatusOf(r.ad_set_status),
  campaignId: r.campaign_id,
  campaignName: r.campaign_name ?? '',
  campaignStatus: entityStatusOf(r.campaign_status),
  campaignEndDate: r.campaign_end_date,
  objective: r.objective,
  currency: trimmedOrNull(r.currency),
  spend: nullableNumber(r.spend),
  impressions: nullableNumber(r.impressions),
  clicks: nullableNumber(r.clicks),
  lastDate: r.last_date,
});

/** Every ad set of the scope with a metric row in the period; [] without a scope (no read). */
export const readAudienceFacts = async (workspaceIds: readonly string[], range: DateRange): Promise<AudienceFact[]> => {
  if (workspaceIds.length === 0) return [];
  const { rows, error } = await readAllRows<AudienceFactDb>((from, to) =>
    getSupabaseClient()
      .rpc('get_audience_facts', { p_workspace_ids: [...workspaceIds], p_start: range.start, p_end: range.end })
      .order('ad_set_id', { ascending: true })
      .range(from, to), 'audience facts read');
  if (error) throw new Error(`audience facts read failed: ${errorMessageOf(error)}`);
  return rows.map(audienceFactOf);
};
